import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Box, CircularProgress, InputLabel, Stack, TextField, Typography } from '@mui/material';

import ShareOfRefund from './ShareOfRefund';

interface Props {
  vendorContribution?: number;
  LEContribution: number;
  onVendorContributionChange: (newValue: string) => void;
  costPrice: number;
  costPriceSell?: number;
  customerRefundAmount?: string;
  currencyCode?: string;
  vendorCurrencyCode?: string;
  isConvertingCostPrice?: boolean;
}

export default function EditableShareOfRefund({
  vendorContribution,
  LEContribution,
  onVendorContributionChange,
  costPrice,
  costPriceSell,
  customerRefundAmount,
  currencyCode,
  vendorCurrencyCode,
  isConvertingCostPrice,
}: Props) {
  const currenciesDiffer = Boolean(vendorCurrencyCode && currencyCode && vendorCurrencyCode !== currencyCode);
  const vendorCurrency = vendorCurrencyCode || currencyCode;
  const fxRatio = useMemo(() => {
    return costPriceSell && costPriceSell > 0 ? costPrice / costPriceSell : undefined;
  }, [costPrice, costPriceSell]);
  const fxRatioText = useMemo(() => (fxRatio ? fxRatio.toFixed(5) : ''), [fxRatio]);
  const reverseFxRatioText = useMemo(() => {
    return fxRatio && fxRatio > 0 ? (1 / fxRatio).toFixed(5) : '';
  }, [fxRatio]);
  const initialString = useMemo(() => {
    const vendorSell = Number.isFinite(vendorContribution) ? vendorContribution : undefined;
    if (!Number.isFinite(vendorSell)) {
      return '';
    }
    const ratio = currenciesDiffer && fxRatio ? fxRatio : 1;
    return (vendorSell * ratio).toFixed(2);
  }, [vendorContribution, currenciesDiffer, fxRatio]);
  const [internalCustomVendorContribution, setInternalCustomVendorContribution] = useState<string>(initialString);
  const [isEditing, setIsEditing] = useState(false);

  const maxVendorContribution = useMemo(() => {
    if (!currenciesDiffer) {
      return customerRefundAmount ? Math.min(parseFloat(customerRefundAmount), costPrice) : costPrice;
    }
    if (customerRefundAmount && fxRatio) {
      const refundInCost = parseFloat(customerRefundAmount) * fxRatio;
      // Match the precision used by the displayed value and committed contribution.
      return Number(Math.min(costPrice, refundInCost).toFixed(2));
    }
    return costPrice;
  }, [currenciesDiffer, customerRefundAmount, costPrice, fxRatio]);
  // Keep input in sync with calculated value when upstream props change,
  // but do not override while the user is typing
  useEffect(() => {
    if (!isEditing) {
      setInternalCustomVendorContribution(initialString);
    }
  }, [initialString, isEditing]);

  const debouncedUpdate = useMemo(
    () =>
      debounce((valueStr: string) => {
        const parsed = parseFloat(valueStr || '');
        const clamped = Math.min(Math.max(0, Number.isFinite(parsed) ? parsed : 0), maxVendorContribution);
        onVendorContributionChange(clamped.toFixed(2));
      }, 400),
    [maxVendorContribution, onVendorContributionChange],
  );

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const updateOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value ?? '';
      setInternalCustomVendorContribution(raw);
      debouncedUpdate(raw);
    },
    [debouncedUpdate],
  );

  const commitNow = useCallback(() => {
    const parsed = parseFloat(internalCustomVendorContribution || '');
    const clamped = Math.min(Math.max(0, Number.isFinite(parsed) ? parsed : 0), maxVendorContribution);
    const fixed = clamped.toFixed(2);
    setInternalCustomVendorContribution(fixed);
    onVendorContributionChange(fixed);
  }, [internalCustomVendorContribution, onVendorContributionChange, maxVendorContribution]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems={currenciesDiffer ? 'flex-end' : undefined}>
        <Stack flex={1}>
          <Stack>
            <InputLabel
              htmlFor="custom-vendor-contribution"
              sx={{
                fontWeight: 'bold',
              }}
            >
              Vendor Contribution ({vendorCurrency})
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
              <TextField
                id="custom-vendor-contribution"
                name="customVendorContribution"
                value={internalCustomVendorContribution}
                hiddenLabel
                type="number"
                onChange={updateOnChange}
                onFocus={() => setIsEditing(true)}
                onBlur={() => {
                  debouncedUpdate.cancel();
                  setIsEditing(false);
                  commitNow();
                }}
                fullWidth
                inputProps={{
                  min: 0,
                  max: maxVendorContribution,
                  step: '.01',
                  inputMode: 'decimal',
                }}
              />
              {isConvertingCostPrice && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
        {currenciesDiffer && (
          <Box alignContent="center">
            {fxRatio && (
              <Box>
                <Typography variant="h6" color="text.secondary">
                  FX: 1 {currencyCode} = {fxRatioText} {vendorCurrency}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  FX: 1 {vendorCurrency} = {reverseFxRatioText} {currencyCode}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {!currenciesDiffer && (
          <Stack flex={1}>
            <InputLabel
              htmlFor="le-contributions-input"
              sx={{
                fontWeight: 'bold',
              }}
            >
              LE Contribution ({currencyCode})
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
              <TextField
                id="le-contributions-input"
                value={LEContribution}
                name="LEContributions"
                type="number"
                variant="filled"
                hiddenLabel
                disabled
                fullWidth
              />
              {isConvertingCostPrice && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
      {currenciesDiffer && (
        <ShareOfRefund
          vendorContribution={vendorContribution || 0}
          LEContribution={LEContribution}
          currencyCode={currencyCode}
          isConvertingCostPrice={isConvertingCostPrice}
        />
      )}
    </Stack>
  );
}
